<template>
    <div>

        <ComponentDialog v-if="showComponentDialog" v-bind="componentDialogParameters"
                         v-on:hidden="showComponentDialog = false"
                         v-on:clear-selection="clearSelection"
                         v-on:click-row="setInstancePath"
                         v-on:select-component="zoomComponent"
                         v-on:zoom-component="zoomExtentsComponent"
                         v-on:zoom-reset="zoomReset"/>

        <div class="card st-framework-card">

            <div class="card-header">
                <table style="width: 100%;">
                    <tr>
                        <td style="margin: 0; padding: 0 0 0 7px; width: 40px;" v-if="environment === 'Local'">
                            <button type="button" class="btn btn-dark btn-sm" v-on:click="reloadPluginFiles(['lp_punch_plates/lp_punch_plates.rb'])">
                                <font-awesome-icon :icon="['fas','sync']"/>
                            </button>
                        </td>
                        <td style="margin: 0; padding: 0 0 0 7px; width: 40px;" v-else>
                            <button type="button" class="btn btn-dark btn-sm" v-on:click="reloadPage()">
                                <font-awesome-icon :icon="['fas','sync']"/>
                            </button>
                        </td>
                        <td style="margin: 0; padding: 0; width: 230px;">
                            {{ $i18n.tc('translations.' + name, 2).toUpperCase() + ' (LP#)' }}
                        </td>
                        <template v-if="serverDataLoaded">
                            <td style="width: 1rem;"/>
                            <template v-if="data.model.project">
                                <td style="padding: 0 10px; border: solid 1px lightgray; width: 30%;" v-if="data.model.phase">
                                    {{ data.model.phase.label }}
                                </td>
                                <td style="width: 30%;" v-else/>
                                <td style="width: 1rem;"/>
                                <td style="padding: 0 10px; border: solid 1px lightgray;">
                                    <LinkAction :action="actions.modelLinkAction"/>
                                </td>
                                <td style="padding: 0; width: 90px; text-align: right;">
                                    (#{{ data.model.phase ? data.model.phase.id : (data.model.order ? data.model.order.id : data.model.project.id) }})
                                </td>
                            </template>
                            <template v-else>
                                <td style="padding: 0 10px; border: solid 1px lightgray;">
                                    <router-link :to="{name: 'order_list'}" class="st-cardheader-link">
                                        {{ $i18n.t('translations.SketchUp model is not linked to a project') }}!
                                    </router-link>
                                </td>
                            </template>
                            <td style="width: 1rem;"/>
                        </template>
                        <td v-else/>
                    </tr>
                </table>
            </div>

            <template v-if="incompatibleSketchUpPlugin">
                <div class="card-body" style="text-align: center; font-size: larger; font-weight: bold; color: red;">
                    <div>{{ $i18n.t("translations.incompatibleSketchUpPluginVersionForModule", {version: this.minimumSketchUpPluginVersion}) }}</div>
                </div>
            </template>
            <template v-else-if="serverDataLoaded">
                <div class="card-body p-0">

                    <div class="card" style="border: none;">
                        <div class="card-header light p-0" style="border: none;">
                            <table style="width: 100%;">
                                <tr>
                                    <td>
                                        <template v-for="(action,actionIndex) in actions.document">
                                            <template v-if="action.placement === 'DOCUMENTHEADERLEFT'">
                                                <ToggleAction :key="action.name+'-'+actionIndex"
                                                              :action="action" :disabled="state.disabled"
                                                              v-on:toggle-auto-zoom="toggleAutoZoom"
                                                              v-on:toggle-viewer="toggleViewer"
                                                              v-if="action.type === 'TOGGLE'"/>
                                            </template>
                                        </template>
                                    </td>
                                    <td>
                                        <div class="float-right">
                                            <template v-for="(action,actionIndex) in actions.document">
                                                <template v-if="action.placement === 'DOCUMENTHEADERRIGHT'">
                                                    <ButtonAction :key="action.name+'-'+actionIndex"
                                                                  :action="action" :disabled="state.disabled"
                                                                  v-on:generate-dxf-files="launchPunchPlates"
                                                                  v-if="action.type === 'BUTTON'"/>
                                                </template>
                                            </template>
                                        </div>
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>

                    <div class="view">
                        <div class="view-picture" v-if="previewMode">
                            <ViewComponent :key="'preview-'+reloadPreviewComponent"
                                           :width="punchPlateViewerWidth" :height="punchPlateViewerHeight"
                                           :component="previewComponent" :vertex-threshold="vertexThreshold"/>
                        </div>
                        <div class="view-table">
                            <Table :key="'component-table-'+reloadTable" card-class="mt-0 mb-0" header-class="light" footer-class="light" body-class=""
                                   with-filter with-pagination pagination-always-visible no-state-loading autofocus
                                   highlight-rows-on-click :highlight-row-id="getHighlightedRowId"
                                   :lines-per-page=data.crudLinesPerPage th-actions-style="width: 99px;"
                                   :rows="filteredComponentList"
                                   :fields="fields.table" :actions="actions.table" :state="state"
                                   :total-fields="fields.tableTotals" :totals="processedComponentTotals"
                                   v-on:clear-selection="clearSelection"
                                   v-on:delete-selection="deleteSelection"
                                   v-on:detail-components="detailComponents"
                                   v-on:export-event="exportLPPunchPlates"
                                   v-on:highlight-row="highlightRow"
                                   v-on:refresh="getLPPunchPlates"
                                   v-on:renumber-definitions="renumberDefinitions"
                                   v-on:select-all="selectAll"
                                   v-on:select-component="selectComponent"
                                   v-on:select-definitions="selectDefinitions"
                                   v-on:select-face-material="selectFaceMaterial"
                                   v-on:select-filter-material="selectFilterMaterial"
                                   v-on:toggle-face-material-choices="toggleFaceMaterialChoices"
                                   v-on:zoom-component="zoomExtentsComponent"
                                   v-on:zoom-reset="zoomReset"/>
                        </div>
                    </div>
                </div>
            </template>

        </div>
    </div>
</template>

<script>
import ButtonAction from "@/components/actions/ButtonAction.vue";
import ComponentDialog from "@/components/dialogs/sketchup/ComponentDialog.vue";
import FileDownload from "@/mixins/FileDownload";
import LinkAction from "@/components/actions/LinkAction.vue";
import SketchUp from "@/mixins/SketchUp";
import Table from "@/components/Table";
import ToggleAction from "@/components/actions/ToggleAction.vue";
import ViewComponent from "@/components/sketchup/ViewComponent_v1_27_0.vue";

/* global sketchup:false */

export default {
    name: 'LPPunchPlates_v1_27_0',
    components: {
        ButtonAction,
        ComponentDialog,
        LinkAction,
        Table,
        ToggleAction,
        ViewComponent,
    },
    props: ['name', 'icon', 'api', 'state', 'environment'],
    mixins: [FileDownload, SketchUp],
    data() {
        return {
            serverDataLoaded: false,
            reloadTable: 0,
            sketchUpToolData: null,
            minimumSketchUpPluginVersion: '1.26.0',
            incompatibleSketchUpPlugin: false,
            data: null,
            fields: null,
            actions: null,
            reloadPreviewComponent: 0,
            autoZoom: false,
            previewMode: true,
            previewComponent: null,
            punchPlateViewerWidth: 640,
            punchPlateViewerHeight: 480,
            vertexThreshold: 10,
            renumberStart: 1,
            rawComponentList: null,
            processedComponentList: [],
            processedComponentTotals: {},
            filteredComponentList: [],
            definitionChoices: [],
            faceMaterialChoice: null,
            faceMaterialChoicesFiltered: true,
            filterMaterialChoice: null,
            showComponentDialog: false,
            componentDialogParameters: {
                title: null,
                actions: null,
                fields: null,
                rows: null,
            },
        }
    },
    computed: {
        getHighlightedRowId: function () {
            if (this.processedComponentList.length > 0) {
                if (this.previewComponent) {
                    let idx = this.processedComponentList.findIndex(component => component.definition === this.previewComponent.definition);
                    if (idx !== -1) {
                        return this.processedComponentList[idx].id;
                    }
                }
                return this.processedComponentList[0].id;
            }
            return null;
        },
    },
    created() {
        window.vm.Tool = this;
        this.getToolData();
    },
    methods: {
        clearSelection() {
            sketchup.clearSelection();
        },
        componentMatchesFilterColor(component) {
            if (!this.filterMaterialChoice?.value) {
                return true;
            }
            let match = false;
            component.instances.forEach(instance => {
                if (instance.color_code === this.filterMaterialChoice.color_code && instance.color_display_name === this.filterMaterialChoice.label) {
                    match = true;
                }
            });
            return match;
        },
        deleteSelection(data) {
            data.ids.forEach(id => {
                let idx = this.processedComponentList.findIndex(component => component.id === id);
                this.processedComponentList.splice(idx, 1);
            });
            this.filter();
        },
        detailComponents(component) {
            let rows = [];
            component.instances.forEach(instance => {
                let definition_path = '';
                instance.definition_path.forEach((d, i) => {
                    definition_path += (definition_path ? ' > ' : '') + d + ' (' + instance.instance_path[i] + ')';
                });
                rows.push({
                    id: rows.length + 1,
                    entity_id: instance.entity_id,
                    instances: [{
                        instance_path: instance.instance_path,
                    }],
                    component_color: {
                        value: instance.color_code,
                        label: instance.color_display_name,
                    },
                    definition_path: definition_path,
                    flipped: instance.is_flipped ? 'X' : '',
                });
            });
            this.componentDialogParameters.title = component.definition;
            this.componentDialogParameters.actions = this.actions.detail;
            this.componentDialogParameters.fields = this.fields.detail;
            this.componentDialogParameters.rows = rows;
            this.showComponentDialog = true;
        },
        exportLPPunchPlates(choice) {
            this.state.loading = true;
            let components = [];
            this.filteredComponentList.forEach(component => {
                if (!choice?.ids?.length || choice.ids.includes(component.id)) {
                    components.push({
                        definition: component.definition,
                        components_in_selection: component.components_in_selection,
                        instances_in_selection: component.instances_in_selection,
                        thickness: component.thickness,
                        area_square_m: component.area_square_m,
                        weight: component.weight,
                        cnt_faces: component.cnt_faces,
                        circles: component.circles,
                        dimensions: component.dimensions ? component.dimensions.join('x').replaceAll('.', ',') : '',
                    });
                }
            });
            this.$http.post(choice.api, {
                'components': components,
            }, {'responseType': 'blob'}).then((res) => {
                this.fileDownload(choice, res.data);
                this.state.loading = false;
            }).catch((error) => {
                console.log("LPPunchPlates:exportLPPunchPlates():error:", error);
            });
        },
        fetchData() {
            this.$http.get(this.api + '/manage_lppunchplates_v1_27_0/' + this.sketchUpToolData.project_id + '/' + this.sketchUpToolData.order_id + '/' + this.sketchUpToolData.phase_id, {}).then((res) => {
                this.actions = res.data.actions;
                this.fields = res.data.fields;
                this.data = res.data.data;
                this.filterTableActionsAndFields();
                this.previewMode = !!parseInt(this.data.previewMode);
                this.autoZoom = !!parseInt(this.data.autoZoom);
                this.punchPlateViewerWidth = parseInt(this.data.punchPlateViewerWidth);
                this.punchPlateViewerHeight = this.punchPlateViewerWidth * 3 / 4;
                this.setFaceMaterialChoices();
                this.serverDataLoaded = true;
                this.state.loading = false;
            }).catch((error) => {
                console.log("LPPunchPlates:fetchData():error:", error);
            });
        },
        filter() {
            this.filteredComponentList = [];
            this.processedComponentList.forEach(component => {
                if (this.componentMatchesFilterColor(component)) {
                    let instances_with_color = 0;
                    if (this.filterMaterialChoice?.value) {
                        component.instances.forEach(instance => {
                            if (instance.color_code === this.filterMaterialChoice.color_code && instance.color_display_name === this.filterMaterialChoice.label) {
                                instances_with_color++;
                            }
                        });
                    }
                    component.instances_with_color = instances_with_color;
                    this.filteredComponentList.push(component);
                }
            }, this);
            this.reloadTable++;
        },
        filterTableActionsAndFields() {
            if (this.actions) {
                this.actions.table.filter(action => action.hideintable = this.previewMode && action.hideinpreview);
            }
            if (this.fields) {
                this.fields.table.filter(field => field.hideintable = this.previewMode && field.hideinpreview);
                this.fields.tableTotals.filter(field => field.hideintable = this.previewMode && field.hideinpreview);
            }
        },
        getLPPunchPlates() {
            this.state.loading = true;
            let faceMaterial = {
                color_code: this.faceMaterialChoice?.color_code,
                display_name: this.faceMaterialChoice?.label,
            }
            if (!this.isVersionGreaterOrEqual(this.sketchUpToolData.plugin_version, '1.27.0')) {
                sketchup.getLPPunchPlates(faceMaterial);
            } else {
                sketchup.getLPPunchPlates(this.definitionChoices, faceMaterial);
            }
        },
        getLPPunchPlatesCallback(entities) {
            this.rawComponentList = JSON.parse(entities);
            this.processComponentList();
        },
        getModelDefinitionsCallback(definitions) {
            let definitionsToRenumber = this.getDefinitionsToRenumber(definitions, ['LP'], this.renumberStart);
            sketchup.renameDefinitions(definitionsToRenumber);
        },
        getToolData() {
            this.state.loading = true;
            sketchup.getToolData();
        },
        getToolDataCallback(data) {
            this.sketchUpToolData = data;
            if (this.isVersionGreaterOrEqual(this.sketchUpToolData.plugin_version, this.minimumSketchUpPluginVersion)) {
                this.fetchData();
            } else {
                this.incompatibleSketchUpPlugin = true;
                this.state.loading = false;
            }
        },
        highlightRow(component) {
            this.previewComponent = component;
            if (this.autoZoom) {
                this.zoomExtentsComponent(this.previewComponent);
            }
            this.reloadPreviewComponent++;
        },
        launchPunchPlates(data) {
            this.state.loading = true;

            // XML Document
            let xml = document.implementation.createDocument(null, "PunchPlates");
            let elements = xml.getElementsByTagName("PunchPlates");

            // Options element
            let options = xml.createElement("Options");
            options.setAttribute("version", this.data.punchPlateUtilityVersion);
            if ('polygon-offset' in data) {
                options.setAttribute("polygonOffset", data['polygon-offset']);
            } else {
                options.setAttribute("polygonOffset", '3');
            }
            if ('filename-prefix' in data) {
                options.setAttribute("fileNamePrefix", data['filename-prefix']);
            } else {
                options.setAttribute("fileNamePrefix", '');
            }
            if ('label-prefix' in data) {
                options.setAttribute("labelPrefix", data['label-prefix']);
            } else {
                options.setAttribute("labelPrefix", '');
            }
            options.setAttribute("targetDir", this.data.dropBoxLocation);
            elements[0].appendChild(options);

            let noRowsSelected = true;
            this.filteredComponentList.forEach(punchplate => {
                if (punchplate.selected) {
                    noRowsSelected = false;
                }
            });
            this.filteredComponentList.forEach(p => {
                if (noRowsSelected || p.selected) {
                    let punchplate = xml.createElement("PunchPlate");
                    punchplate.setAttribute("definition", p.definition);
                    let prefix = data['label-prefix'] ? data['label-prefix'] : p.definition.substring(0, 1);
                    punchplate.setAttribute("label", prefix + this.getDefinitionNumber(p.definition));
                    punchplate.setAttribute("thickness", p.thickness);
                    punchplate.setAttribute("quantity", p.instances_in_selection);
                    let face = xml.createElement("Face");

                    if (p.loops) {
                        p.loops.forEach(l => {
                            let loop = xml.createElement("Loop");
                            loop.setAttribute("outer", l.outer);
                            loop.setAttribute("circle", l.circle);
                            if (l.circle) {
                                let arccurve = xml.createElement("ArcCurve");
                                arccurve.setAttribute("x", l.arccurve.x);
                                arccurve.setAttribute("y", l.arccurve.y);
                                arccurve.setAttribute("radius", l.arccurve.radius);
                                loop.appendChild(arccurve);
                            } else {
                                l.vertices.forEach(v => {
                                    let vertex = xml.createElement("Vertex");
                                    vertex.setAttribute("x", v.x);
                                    vertex.setAttribute("y", v.y);
                                    loop.appendChild(vertex);
                                });
                            }
                            face.appendChild(loop);
                        });
                    }

                    punchplate.appendChild(face);
                    elements[0].appendChild(punchplate);
                }
            });

            let sXml = (new XMLSerializer()).serializeToString(xml);
            sketchup.launchPunchPlates(process.env.VUE_APP_TEMP_DIR, process.env.VUE_APP_PUNCHPLATES_DIR, sXml);
        },
        launchPunchPlatesCallback() {
            this.state.loading = false;
        },
        processComponentList() {
            this.$worker.run((rawComponentList, vertexThreshold) => {
                let newProcessedComponentList = [];
                let newProcessedComponentTotals = {
                    instances_in_selection: 0,
                    area_square_m: 0,
                    weight: 0,
                    circles: '',
                };
                let circleTotals = [];
                let usedMaterials = [];

                rawComponentList.forEach(component => {
                    let newComponent = component;
                    newComponent.id = newProcessedComponentList.length + 1;
                    newComponent.selected = false;

                    if (newComponent.face_error) {
                        newComponent.id_style = "background: red; color: white;";
                        newComponent.cnt_faces_style = "background: red; color: white;";
                    }

                    let ids = [];
                    newComponent.component_color_list = [];
                    newComponent.instances.forEach(instance => {
                        ids.push(instance.entity_id);
                        if (instance.color_code) {
                            if (newComponent.component_color_list.findIndex(color => (color.value === instance.color_code && color.label === instance.color_display_name)) === -1) {
                                newComponent.component_color_list.push({
                                    value: instance.color_code,
                                    label: instance.color_display_name,
                                });
                            }
                            if (usedMaterials.findIndex(material => (material.color_code === instance.color_code && material.label === instance.color_display_name)) === -1) {
                                usedMaterials.push({
                                    'icon': ['fas', 'circle'],
                                    'iconsize': 'xs',
                                    'iconstyle': 'color:' + instance.color_code,
                                    'value': instance.color_code + instance.color_display_name,
                                    'label': instance.color_display_name,
                                    'color_code': instance.color_code,
                                });
                            }
                        }
                    });
                    usedMaterials.sort((a, b) => (a.label > b.label) ? 1 : ((a.label < b.label) ? -1 : 0));

                    newComponent.components_in_selection = [...new Set(ids)].length;
                    newComponent.instances_in_selection = newComponent.instances.length;
                    newComponent.thickness_f = newComponent.thickness ? newComponent.thickness.toFixed(1).replace('.', ',') : '';
                    let area_square_m = newComponent.instances_in_selection * (newComponent.area ? newComponent.area : 0) / 1000000;
                    newComponent.area_square_m = area_square_m;
                    newComponent.area_square_m_f = area_square_m.toFixed(2).replace('.', ',');
                    newProcessedComponentTotals.area_square_m += area_square_m;
                    let weight = Math.round(100 * 7850 * area_square_m * (newComponent.thickness ? newComponent.thickness : 0) / 1000) / 100;
                    component.weight = weight;
                    component.weight_f = weight ? weight.toFixed(2).replace('.', ',') : '';
                    newProcessedComponentTotals.weight += weight;

                    if (newComponent.loops) {
                        let circles = [];
                        newComponent.loops.forEach(loop => {
                            if (loop.circle) {
                                let diameter = Math.round(loop.arccurve.radius * 200) / 100;
                                let i = circles.findIndex(circle => circle.diameter === diameter);
                                if (i === -1) {
                                    circles.push({
                                        'diameter': diameter,
                                        'count': 1,
                                    })
                                } else {
                                    circles[i].count++;
                                }
                            }
                            if (!loop.circle && !loop.outer) {
                                if (loop.vertices.length >= vertexThreshold) {
                                    newComponent.id_style = newComponent.id_style ? newComponent.id_style : "background: orange; color: white;";
                                    newComponent.circles_style = "background: orange; color: white;";
                                }
                            }
                        });
                        circles.sort((a, b) => a.diameter - b.diameter);
                        newComponent.circles = '';
                        circles.forEach(circle => {
                            let i = circleTotals.findIndex(t => t.diameter === circle.diameter);
                            let count = newComponent.instances_in_selection * circle.count;
                            if (i === -1) {
                                circleTotals.push({
                                    'diameter': circle.diameter,
                                    'count': count,
                                })
                            } else {
                                circleTotals[i].count += count;
                            }
                            let c = circle.count + ' x ' + circle.diameter.toString().replace('.', ',') + 'mm';
                            newComponent.circles += (newComponent.circles ? ', ' + c : c);
                        });
                    }

                    newProcessedComponentList.push(newComponent);
                    newProcessedComponentTotals.instances_in_selection += newComponent.instances_in_selection;
                });

                newProcessedComponentTotals.area_square_m_f = newProcessedComponentTotals.area_square_m.toFixed(2).replace('.', ',');
                newProcessedComponentTotals.weight_f = newProcessedComponentTotals.weight.toFixed(2).replace('.', ',');

                newProcessedComponentList.sort(function (a, b) {
                    if (a.definition.substring(0, 3) === 'LP#' &&
                        b.definition.substring(0, 3) === 'LP#' &&
                        !isNaN(parseInt(a.definition.substring(3))) &&
                        !isNaN(parseInt(b.definition.substring(3)))) {
                        if (parseInt(a.definition.substring(3)) < parseInt(b.definition.substring(3))) return -1;
                        if (parseInt(a.definition.substring(3)) > parseInt(b.definition.substring(3))) return 1;
                    } else {
                        if (a.definition < b.definition) return -1;
                        if (a.definition > b.definition) return 1;
                    }
                    return 0;
                });

                circleTotals.sort((a, b) => a.diameter - b.diameter);
                circleTotals.forEach(circle => {
                    let c = circle.count + ' x ' + circle.diameter.toString().replace('.', ',') + 'mm';
                    newProcessedComponentTotals.circles += (newProcessedComponentTotals.circles ? ', ' + c : c);
                });

                return [
                    newProcessedComponentList,
                    newProcessedComponentTotals,
                    usedMaterials
                ];
            }, [this.rawComponentList, this.vertexThreshold]).then(data => {
                this.processedComponentList = data[0];
                this.processedComponentTotals = data[1];
                let usedMaterials = data[2];

                let actionIdx = this.actions.table.findIndex(action => action.name === 'select-filter-material');
                usedMaterials = [this.actions.table[actionIdx].choices[0]].concat(usedMaterials)
                this.actions.table[actionIdx].choices = usedMaterials;

                let choiceIdx = usedMaterials.findIndex(choice => (choice.label === this.filterMaterialChoice?.label && choice.color_code === this.filterMaterialChoice?.color_code));
                this.filterMaterialChoice = (choiceIdx === -1 ? usedMaterials[0] : usedMaterials[choiceIdx]);
                this.actions.table[actionIdx].selected = this.filterMaterialChoice.value;

                this.filter();
                this.state.loading = false;
            }).catch(error => {
                console.log("LPPunchPlates:processComponentList():error:", error);
            });
        },
        renameDefinitionsCallback() {
            this.getLPPunchPlates();
        },
        renderingOptions(show) {
            sketchup.renderingOption('InactiveHidden', show);
            sketchup.renderingOption('InstanceHidden', show);
        },
        renumberDefinitions(data) {
            this.state.loading = true;
            this.renumberStart = data.start;
            sketchup.getModelDefinitions();
        },
        selectAll(data) {
            let ids = [];
            this.filteredComponentList.forEach(component => {
                if (data.ids.includes(component.id)) {
                    component.instances.forEach(instance => {
                        ids.push(instance.entity_id);
                    });
                }
            });
            sketchup.selectComponents(ids);
        },
        selectComponent(component) {
            let ids = [];
            component.instances.forEach(instance => {
                if (!this.filterMaterialChoice?.value || (instance.color_code === this.filterMaterialChoice.color_code && instance.color_display_name === this.filterMaterialChoice.label)) {
                    ids.push(instance.entity_id);
                }
            }, this);
            sketchup.selectComponents(ids);
        },
        selectDefinitions(choices) {
            this.definitionChoices = choices.filter(choice => choice.value === true).map(choice => choice.label);
        },
        selectFaceMaterial(choice) {
            this.faceMaterialChoice = choice;
        },
        selectFilterMaterial(choice) {
            this.filterMaterialChoice = choice;
            this.filter();
        },
        setFaceMaterialChoices() {
            let materialChoices = [];
            this.sketchUpToolData.materials.forEach(material => {
                if (!this.faceMaterialChoicesFiltered || material.display_name.toLowerCase().includes('mm')) {
                    if (materialChoices.findIndex(choice => choice.label === material.display_name && choice.color_code + material.color_code) === -1) {
                        materialChoices.push({
                            'icon': ['fas', 'circle'],
                            'iconsize': 'xs',
                            'iconstyle': 'color:' + material.color_code,
                            'value': material.color_code + material.display_name,
                            'label': material.display_name,
                            'color_code': material.color_code,
                        });
                    }
                }
            });
            materialChoices.sort((a, b) => (a.label > b.label) ? 1 : ((a.label < b.label) ? -1 : 0));

            let actionIdx = this.actions.table.findIndex(action => action.name === 'select-face-material');
            materialChoices = [this.actions.table[actionIdx].choices[0]].concat(materialChoices);
            this.actions.table[actionIdx].choices = materialChoices;

            let choiceIdx = materialChoices.findIndex(choice => (choice.label === this.faceMaterialChoice?.label && choice.color_code === this.faceMaterialChoice?.color_code));
            this.faceMaterialChoice = (choiceIdx === -1 ? materialChoices[0] : materialChoices[choiceIdx]);
            this.actions.table[actionIdx].selected = this.faceMaterialChoice.value;

            this.reloadTable++;
        },
        setInstancePath(component) {
            sketchup.setInstancePath(component.instances[0].instance_path);
        },
        toggleAutoZoom(action) {
            this.autoZoom = action.toggled;
            if (this.autoZoom) {
                this.zoomExtentsComponent(this.previewComponent);
            }
        },
        toggleFaceMaterialChoices(action) {
            this.faceMaterialChoicesFiltered = action.toggled
            this.setFaceMaterialChoices();
        },
        toggleViewer(action) {
            this.previewMode = action.toggled;
            this.filterTableActionsAndFields();
            this.reloadTable++;
        },
        zoomComponent(component) {
            sketchup.zoomByInstancePath(component.instances[0].instance_path);
        },
        zoomExtentsComponent(component) {
            if (component) {
                this.renderingOptions(true);
                sketchup.zoomExtentsByInstancePath(component.instances[0].instance_path);
            }
        },
        zoomReset() {
            this.renderingOptions(false);
            sketchup.zoomExtents();
        },
    }
}
</script>

<style scoped>
.view {
    display: flex;
    margin: 0 0 1rem 0;
}

.view-picture {
    display: inline-block;
    margin: 0 0 0 1rem;
}

.view-table {
    width: 100%;
    display: inline-block;
}
</style>
